<script setup>
import FooterComponent from '@/components/Footer.vue'
import { useHead } from '@unhead/vue'
import { computed } from 'vue'
import { useStore } from 'vuex'
import SvgIcon from '@/components/SvgIcon.vue'

const headTitle = `${import.meta.env.VITE_BRAND_NAME} | Cancellation Policy`
const headDescription =
  'Read our Cancellation Policy for detailed information on service termination, refunds, and user agreements. Stay informed about your options.'
const headUrl = `https://${import.meta.env.VITE_DOMAIN_NAME}/cancellation-policy`
const headImage = `https://${import.meta.env.VITE_DOMAIN_NAME}/twitter-card.png`
const headLogo = `https://${import.meta.env.VITE_DOMAIN_NAME}/logo.png`

useHead({
  title: `${import.meta.env.VITE_BRAND_NAME} | Cancellation Policy`,
  meta: [
    { name: 'description', content: headDescription },

    { property: 'og:title', content: headTitle },
    { property: 'og:description', content: headDescription },
    { property: 'og:url', content: headUrl },
    { property: 'og:image', content: headImage },
    { property: 'og:logo', content: headLogo },

    { name: 'twitter:title', content: headTitle },
    { name: 'twitter:description', content: headDescription },
    { name: 'twitter:url', content: headUrl },
    { name: 'twitter:image', content: headImage },

    {
      name: 'robots',
      content: 'noindex',
    },
  ],
})

const $store = useStore()
const discountPopupOpen = computed(() => $store.state.discountPopupOpen)

const brandName = import.meta.env.VITE_BRAND_NAME
const supportEmail = import.meta.env.VITE_SUPPORT_EMAIL
</script>

<template>
  <div class="w-full hidden lg:block absolute z-10 left-0 top-0">
    <SvgIcon icon-id="legal-banner" class="w-full h-64" />
    <div
      class="absolute left-0 top-[85px] bottom-0 w-full"
      style="
        background: linear-gradient(
          180deg,
          rgba(7, 9, 23, 0) 0%,
          #070917 80.22%
        );
      "
    ></div>
  </div>
  <div class="bg-[#070917] lg:ml-[280px] pb-[131px] lg:pb-[45px]">
    <div>
      <div class="px-4 xl:px-16 3xl:mx-auto m-auto max-w-[1450px]">
        <p
          class="relative text-[25px] z-20"
          :class="discountPopupOpen ? 'pt-[90px]' : 'lg:pt-[18px]'"
        >
          Cancellation Policy
        </p>
        <p class="relative mt-[15px] mb-[33px] text-white text-opacity-70 z-20">
          Policy version: 24th of July, 2025
        </p>
        <div
          class="bg-[#0E0D20] py-10 px-[41px] rounded-[25px] border border-white border-opacity-5 relative z-20"
        >
          <p class="text-[26px] mb-10">Cancellation Policy</p>
          <p class="mb-10 text-[19px]">
            This website is supplied by <span>{{ brandName }}</span> ("we",
            "us", and "our").
          </p>
          <p class="mb-10 text-[19px]">What this policy applies to</p>
          <p class="text-[14px] text-[#B1B5DB]">
            This cancellation policy relates to your use of the website.
          </p>
          <p class="my-10 text-[19px]">
            Subscription Cancellation within 24 hours
          </p>
          <ol>
            <li class="text-[14px] text-[#B1B5DB]">
              Customers have the right to cancel their subscription and request
              a full refund within 24 hours of making their payment, given that
              they have not spent more than 20 luna.
            </li>
          </ol>
          <p class="text-[19px] my-10">
            Subscription Cancellation after 24 Hours
          </p>
          <ol>
            <li class="text-[14px] text-[#B1B5DB]">
              Subscription cancellation requests made after the 24-hour period
              will be effective at the end of the current billing period.
            </li>
          </ol>
          <p class="text-[19px] my-10">Request a Subscription Cancellation</p>
          <ol class="max-[700px]:pl-3 max-[500px]:pl-2">
            <li class="mb-3 text-[14px] text-[#B1B5DB]">
              To request a refund, customers should send an email to
              <!--email_off-->{{ supportEmail
              }}<!--/email_off-->
              detailing their request.
            </li>
            <li class="mb-3 text-[14px] text-[#B1B5DB]">
              Refund requests must include the customer's account information
              and the reason for cancellation.
            </li>
          </ol>
          <p class="my-10 text-[19px]">Luna Package Cancellation</p>
          <ol>
            <li class="text-[14px] text-[#B1B5DB]">
              All Luna Package purchases are final and non-refundable.
            </li>
          </ol>
          <p class="my-10 text-[19px]">Recurring Payment Cancellation</p>
          <ol>
            <li class="text-[14px] text-[#B1B5DB]">
              All recurring (monthly and yearly) subscription plan payments are
              refundable within 24 hours, given that the user has not spent more
              than 20 luna allocated from that payment.
            </li>
          </ol>
          <p class="my-10 text-[19px]">Cryptocurrency Transactions</p>
          <ol>
            <li class="text-[14px] text-[#B1B5DB]">
              All cryptocurrency transactions are final and non-refundable.
            </li>
          </ol>
        </div>
        <FooterComponent />
      </div>
    </div>
  </div>
</template>

<style scoped>
ol:not(.dont-count) {
  counter-reset: item;
}
li:not(.dont-count-li) {
  display: block;
}
li:not(.dont-count-li):before {
  content: counters(item, '.') ' ';
  counter-increment: item;
}
a {
  text-decoration: underline;
}
</style>
